import Router from 'vue-router';
import { CareType } from '@/types/care_type';
import { navigateToReturnPath, isInvalidReturnPath } from './routerNavigation';

export interface JobPostRedirectOptions {
  source?: string;
  reservationId?: string;
  returnPath?: string;
}

/**
 * Determines if a care type is a child care type (babysitter, nanny, or child_care).
 * Child care types use the SPA route /job-post, while non-child care types use server-side routes.
 */
function isChildCareType(careType: string | undefined): boolean {
  if (!careType) return false;
  const childCareTypes = [CareType.BABYSITTER.toLowerCase(), CareType.NANNY.toLowerCase(), 'child_care'];
  return childCareTypes.includes(careType);
}

/**
 * Builds the redirect URL for non-child care types.
 * These care types use the server-side route /job_posting/new with a care_type query parameter.
 */
function buildNonChildCareRedirectUrl(careType: string | undefined): string {
  const careTypeToUrlMap: Record<string, string> = {
    [CareType.HOUSEKEEPING.toLowerCase()]: '/job_posting/new?care_type=housekeeping',
    [CareType.PET_CARE.toLowerCase()]: '/job_posting/new?care_type=pet_care',
    [CareType.SPECIAL_NEEDS.toLowerCase()]: '/job_posting/new?care_type=special_needs',
    [CareType.COMPANION_CARE.toLowerCase()]: '/job_posting/new?care_type=companion_care',
  };

  return careTypeToUrlMap[careType || ''] || '/';
}

/**
 * Appends the returnPath query parameter to a base URL if it exists and is valid.
 * Handles both URL construction and manual string concatenation as fallback.
 * When an explicit returnPath is provided, it is used without validation to allow callers
 * to override the default validation rules.
 */
function appendReturnPathIfValid(baseUrl: string, router: Router, explicitReturnPath?: string): string {
  const returnPath = explicitReturnPath || (router.currentRoute.query.returnPath as string | undefined);

  if (!returnPath) {
    return baseUrl;
  }

  // Only validate returnPath from router query, not explicit returnPath from options
  if (!explicitReturnPath && isInvalidReturnPath(returnPath)) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('returnPath', returnPath);
    return url.pathname + url.search;
  } catch {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}returnPath=${encodeURIComponent(returnPath)}`;
  }
}

/**
 * Redirects to the appropriate job posting route based on care type.
 *
 * For child care types (BABYSITTER, NANNY, CHILD_CARE):
 * - Uses the SPA route /job-post via router.push
 * - Supports optional query parameters like source and reservationId
 * - Handles returnPath via navigateToReturnPath
 *
 * For non-child care types (HOUSEKEEPING, PET_CARE, SPECIAL_NEEDS, COMPANION_CARE):
 * - Uses the server-side route /job_posting/new?care_type=<type> via window.location.href
 * - Appends returnPath query parameter if valid
 * - Preserves browser history so users can navigate back
 *
 * @param careType - The care type to redirect for (CareType enum or 'CHILD_CARE')
 * @param router - Vue Router instance for navigation
 * @param options - Optional parameters like source or reservationId
 */
export async function redirectToJobPost(
  careType: CareType | 'CHILD_CARE',
  router: Router,
  options?: JobPostRedirectOptions
): Promise<void> {
  const normalizedCareType = careType?.toLowerCase();
  const isChildCare = isChildCareType(normalizedCareType);

  if (isChildCare) {
    let defaultPath = '/job-post';

    if (options?.reservationId) {
      defaultPath = `/job-post?reservationId=${options.reservationId}`;
    } else if (options?.source) {
      defaultPath = `/job-post?source=${options.source}`;
    }

    navigateToReturnPath(router, defaultPath);
    return;
  }

  const redirectUrl = buildNonChildCareRedirectUrl(normalizedCareType);
  const finalUrl = appendReturnPathIfValid(redirectUrl, router, options?.returnPath);

  window.location.href = finalUrl;
}
